/** Play-chip no-limit Texas Hold’em. The server owns all cards and chip movement. */
export const HOLDEM_MODULE_ID = 'holdem';
export const HOLDEM_EVENTS = {
  STATE: 'holdem:state',
  HAND: 'holdem:hand',
  ACTION: 'holdem:action',
  SNAPSHOT: 'holdem:snapshot',
  LEAVE: 'holdem:leave',
  ERROR: 'holdem:error',
  DEALER: 'holdem:dealer',
  PLAYER_VOICE: 'holdem:player-voice',
} as const;
export interface HoldemCard {
  rank: number;
  suit: 'S' | 'H' | 'D' | 'C';
}
export type HoldemPhase = 'lobby' | 'preflop' | 'flop' | 'turn' | 'river' | 'finished';
export type HoldemMove = 'fold' | 'check' | 'call' | 'raise' | 'allin';
export interface HoldemPlayer {
  id: string;
  name: string;
  connected: boolean;
  /** Autonomous server-controlled seat, not a room/media participant. */
  ai?: boolean;
  aiPersona?: HoldemPersonaId;
  aiStyle?: 'balanced' | 'bold' | 'careful';
  stack: number;
  bet: number;
  committed: number;
  status: 'waiting' | 'active' | 'folded' | 'allin';
  revealed: HoldemCard[];
  /** Buy-ins of HOLDEM_BUY_IN this seat has taken, the first one included. Absent from older servers: read as 1. */
  buyIns?: number;
}
export interface HoldemLastAction {
  playerId: string;
  move: HoldemMove;
  amount: number;
  /** The turn the move was accepted on; the snapshot's own `turn` is the one after it. */
  turn: number;
}
export interface HoldemState {
  roomId: string;
  gameId: string;
  revision: number;
  turn: number;
  serverNow: number;
  phase: HoldemPhase;
  handNumber: number;
  players: HoldemPlayer[];
  spectatorIds: string[];
  board: HoldemCard[];
  buttonId: string | null;
  smallBlindId: string | null;
  bigBlindId: string | null;
  currentPlayerId: string | null;
  currentBet: number;
  minRaiseTo: number;
  /**
   * Bets and raises accepted on the current street, all-ins that raised the price included; the
   * blinds are not raises. Public: every seat saw each one. Absent from older servers.
   */
  raisesThisRound?: number;
  pot: number;
  paused: boolean;
  lastEvent: string;
  /**
   * The accepted betting move `lastEvent` describes, or null when `lastEvent` came from anything
   * else (a deal, a top-up, a reminder, a host end). Narration reads the move from here rather
   * than parsing the sentence. `amount` is the call paid, or the bet/raise/all-in total; 0 for a
   * check or fold. Absent from older servers.
   */
  lastAction?: HoldemLastAction | null;
  payouts: {
    playerId: string;
    amount: number;
    hand: string;
    /** The five cards that made the winning hand. Absent when the pot was won uncontested. */
    cards?: HoldemCard[];
  }[];
  /**
   * Best-hand name ('One pair', 'Flush', …) by player id, for every seat whose cards were turned
   * face up at this hand's showdown, losers included. Public: it names only revealed cards plus
   * the board. Never set for a folded seat or an uncontested pot; cleared when the next hand is
   * dealt. Absent from older servers, and whenever there was no showdown.
   */
  showdownHands?: Record<string, string>;
  banter: boolean;
  commentary: boolean;
  playerVoices?: boolean;
  voices?: HoldemPlayerVoices;
  /**
   * Short-lived faces, by player id, derived on the server from PUBLIC table moments (an all-in,
   * a bad beat, a bust, a confessed bluff once it is spoken). Only entries still showing at
   * `serverNow` are sent; a client stops showing one at `until`. Absent from older servers.
   */
  seatEmotions?: Record<string, HoldemSeatEmotion>;
}
/** Emotion names understood by the avatar's EmotionMorphBlender. */
export type HoldemEmotion =
  | 'excited'
  | 'amused'
  | 'surprised'
  | 'frustrated'
  | 'sad'
  | 'skeptical'
  | 'sympathetic'
  | 'concerned';
export interface HoldemSeatEmotionStage {
  emotion: HoldemEmotion;
  /** 0–1, from the moment's salience. */
  intensity: number;
  /** Server epoch ms this stage stops showing. */
  until: number;
}
export interface HoldemSeatEmotion extends HoldemSeatEmotionStage {
  /** Increases with every emotion the table sets; a new one replaces the same seat's older one. */
  seq: number;
  /** A follow-on stage shown from `until` to `then.until` (a winner's excited, then amused). */
  then?: HoldemSeatEmotionStage;
}
export interface HoldemHand {
  roomId: string;
  gameId: string;
  revision: number;
  turn: number;
  cards: HoldemCard[];
  moves: HoldemMove[];
  callAmount: number;
  minRaiseTo: number;
  maxRaiseTo: number;
}
export type HoldemAction = { roomId: string; gameId: string; turn: number } & (
  | { type: HoldemMove; amount?: number }
  | { type: 'deal' | 'end' | 'rebuy' }
  | { type: 'player-voices'; enabled: boolean; voices?: HoldemPlayerVoices }
  | { type: 'ai-players'; count: number }
  | { type: 'configure'; banter: boolean; commentary: boolean }
);
/** Play chips a seat starts with, and each top-up. */
export const HOLDEM_BUY_IN = 1000;
/** The big blind; the small blind is half of it. */
export const HOLDEM_BIG_BLIND = 20;
/**
 * A seat's running result: its chips, counting what it has in the pot this hand, less every buy-in.
 * Chips in the pot are not lost until the hand is decided, so a bet does not move it.
 */
export function holdemNet(p: Pick<HoldemPlayer, 'stack' | 'committed' | 'buyIns'>): number {
  return p.stack + p.committed - HOLDEM_BUY_IN * (p.buyIns ?? 1);
}
export function holdemCardName(c: HoldemCard): string {
  return `${({ 11: 'J', 12: 'Q', 13: 'K', 14: 'A' } as Record<number, string>)[c.rank] ?? c.rank}${{ S: '♠', H: '♥', D: '♦', C: '♣' }[c.suit]}`;
}

/** Stock Inworld voices already used by the room/admin character catalog. */
export const HOLDEM_AI_PERSONAS = [
  {
    id: 'ace',
    name: 'Ace',
    personality: 'Cool, dry-witted card shark. Understated one-liners; never mean.',
    voice: 'Craig',
    style: 'balanced',
  },
  {
    id: 'blaze',
    name: 'Blaze',
    personality: 'Larger-than-life showman. Big theatrical bravado; laughs at their own mistakes.',
    voice: 'Hank',
    style: 'bold',
  },
  {
    id: 'cleo',
    name: 'Cleo',
    personality:
      'Calm, precise strategist with deadpan wit. Teases dramatic betting, never explains hidden information.',
    voice: 'Lauren',
    style: 'careful',
  },
  {
    id: 'dash',
    name: 'Dash',
    personality:
      'Quick, mischievous wisecracker. Playful sports-style trash talk and cheerful self-roasts.',
    voice: 'Brandon',
    style: 'bold',
  },
  {
    id: 'echo',
    name: 'Echo',
    personality: 'Warm, upbeat wildcard. Celebrates rivals, then slips in a cheeky joke.',
    voice: 'Zoe',
    style: 'balanced',
  },
] as const;
export type HoldemPersonaId = (typeof HOLDEM_AI_PERSONAS)[number]['id'];
/**
 * The character each persona wears at the table (#4863), by `/api/characters` id. Only the look:
 * the voice stays the persona's. A persona mapped to null, or to an id the catalog does not list,
 * keeps its drawn portrait. Proposed casting, matched to each voice; the owner casts (D-P1).
 */
export const HOLDEM_PERSONA_CHARACTERS: Record<HoldemPersonaId, string | null> = {
  ace: 'jules',
  blaze: 'rick',
  cleo: 'jan',
  dash: 'ken',
  echo: 'mae',
};
export type HoldemPlayerVoices = Partial<Record<HoldemPersonaId, string>>;
/** Same stock IDs and workspace/UUID IDs accepted by the shared Inworld picker. */
export function validHoldemVoice(voice: unknown): voice is string {
  return (
    typeof voice === 'string' &&
    voice.length <= 128 &&
    (/^[A-Za-z][A-Za-z0-9_-]*$/.test(voice) ||
      /^[a-zA-Z0-9_-]+__[a-zA-Z0-9_-]+$/.test(voice) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(voice))
  );
}
export function holdemPlayerVoices(input?: unknown): HoldemPlayerVoices {
  const values = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  return Object.fromEntries(
    HOLDEM_AI_PERSONAS.map(p => [p.id, validHoldemVoice(values[p.id]) ? values[p.id] : p.voice])
  );
}
